/**
 * Affiliate attribution — client-side capture of `?ref={username}`.
 *
 * Flow:
 *   1. Any page load: if `?ref=` is present and looks like a valid username,
 *      we persist `{username, landing_path, utm, first_touch_at}` to
 *      localStorage with a 30-day TTL (display side). The server holds the
 *      authoritative 90-day window.
 *   2. After signup completes (in `Auth.tsx` once a session exists, and in
 *      `UsernameOnboarding.tsx` after backfill / OAuth signup), we call
 *      `attributeSignupFromStorage()` which forwards the captured payload to
 *      the `attribute_signup_to_affiliate` RPC. Self-referrals and unknown
 *      usernames resolve to no-ops server-side.
 *
 * First touch wins: the RPC enforces a UNIQUE on `referred_user_id`. Re-clicks
 * after attribution don't overwrite.
 */
import { supabase } from "@/integrations/supabase/client";

export const REF_STORAGE_KEY = "excerptus_ref";
export const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days (display side)

const USERNAME_RE = /^[a-z0-9_]{3,24}$/;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export type StoredAttribution = {
  username: string;
  landing_path: string | null;
  utm: Record<string, string>;
  first_touch_at: string; // ISO
};

function isUsername(value: string): boolean {
  return USERNAME_RE.test(value);
}

function readStorage(): StoredAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(REF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution & { _saved_at?: number };
    if (typeof parsed?._saved_at === "number" && Date.now() - parsed._saved_at > REF_TTL_MS) {
      window.localStorage.removeItem(REF_STORAGE_KEY);
      return null;
    }
    if (typeof parsed?.username !== "string" || !isUsername(parsed.username)) {
      window.localStorage.removeItem(REF_STORAGE_KEY);
      return null;
    }
    return {
      username: parsed.username,
      landing_path: parsed.landing_path ?? null,
      utm: parsed.utm ?? {},
      first_touch_at: parsed.first_touch_at ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function writeStorage(payload: StoredAttribution) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      REF_STORAGE_KEY,
      JSON.stringify({ ...payload, _saved_at: Date.now() }),
    );
  } catch {
    // localStorage unavailable (private mode, quota) — silently skip.
  }
}

export function clearStoredAttribution() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(REF_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Inspect the current URL for `?ref=` and persist if valid. Idempotent — a
 * later visit without `?ref=` does NOT clear the existing record (first
 * touch wins). A second `?ref=` visit before signup overwrites the previous
 * value (so the latest landing path / UTMs are kept) but the client never
 * gets a vote once attribution lands server-side.
 */
export function captureReferralFromLocation(loc: { search: string; pathname: string } | null = null) {
  if (typeof window === "undefined") return;
  const target = loc ?? { search: window.location.search, pathname: window.location.pathname };
  const params = new URLSearchParams(target.search);
  const raw = params.get("ref");
  if (!raw) return;
  const username = raw.trim().toLowerCase();
  if (!isUsername(username)) return;

  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) utm[key] = v;
  }

  writeStorage({
    username,
    landing_path: target.pathname || "/",
    utm,
    first_touch_at: new Date().toISOString(),
  });
}

/**
 * Send any stored attribution to the server. Safe to call multiple times —
 * server enforces first-touch-wins via UNIQUE(referred_user_id).
 *
 * Returns the attribution row id, or null when there is nothing to attribute
 * (no stored ref, malformed, or self-referral / unknown username).
 */
export async function attributeSignupFromStorage(): Promise<string | null> {
  const stored = readStorage();
  if (!stored) return null;
  try {
    const { data, error } = await supabase.rpc("attribute_signup_to_affiliate", {
      _referral_username: stored.username,
      _landing_path: stored.landing_path,
      _utm: stored.utm,
      _first_touch_at: stored.first_touch_at,
    });
    if (error) {
      console.warn("[affiliate] attribution rpc failed", error.message);
      return null;
    }
    // Whether or not a row was inserted (could be self-referral / unknown user),
    // the click was "consumed" by this user — clear local storage to avoid
    // re-sending on every subsequent page load.
    clearStoredAttribution();
    return (data as string | null) ?? null;
  } catch (err) {
    console.warn("[affiliate] attribution threw", err);
    return null;
  }
}
